import * as React from "react";
import {
  ActivityIcon,
  BlocksIcon,
  BotIcon,
  ChartSplineIcon,
  CheckSquareIcon,
  Frame,
  GitBranchIcon,
  Globe as GlobeIcon,
  HandshakeIcon,
  HeadsetIcon,
  HomeIcon,
  type LucideIcon,
  Map,
  MessageSquareIcon,
  NotebookPenIcon,
  PieChart,
  UserPlusIcon,
  UsersIcon,
} from "lucide-react";

import { DashboardNavMain } from "@/components/pages/dashboard/DashboardNavMain";
import { DashboardNavUser } from "@/components/pages/dashboard/DashboardNavUser";
import { SidebarOrganisationCard } from "@/components/pages/dashboard/SidebarOrganisationCard";
import { Sidebar, SidebarContent, SidebarFooter, SidebarHeader, SidebarRail } from "@/components/ui/sidebar";
import { useGetFeatureCatalogQuery, useGetHomeQuery } from "@/store/rest_api";
import type { FeatureKey } from "@/model/interface/Feature";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { SidebarSettingsMenu } from "@/components/pages/dashboard/SidebarSettingsMenu";
import { AdminMenu } from "@/components/pages/dashboard/AdminMenu";

type NavItem = {
  title: string;
  url: string;
  icon: LucideIcon;
  isActive?: boolean;
  feature?: FeatureKey;
};

const data: { navMain: NavItem[]; projects: Array<{ name: string; url: string; icon: LucideIcon }> } = {
  navMain: [
    {
      title: "Dashboard",
      url: "/dashboard",
      icon: ChartSplineIcon,
      isActive: true,
    },
    {
      title: "Inbox",
      url: "/inbox",
      icon: MessageSquareIcon,
      isActive: true,
    },
    {
      title: "Contacts",
      url: "/contacts",
      icon: UsersIcon,
    },
    {
      title: "Leads",
      url: "/leads",
      icon: UserPlusIcon,
    },
    {
      title: "Deals",
      url: "/deals",
      icon: HandshakeIcon,
    },
    {
      title: "Properties",
      url: "/properties",
      icon: HomeIcon,
    },
    {
      title: "Tasks",
      url: "/tasks",
      icon: CheckSquareIcon,
    },
    {
      title: "Workflows",
      url: "/workflows",
      icon: GitBranchIcon,
    },
    {
      title: "Campaigns",
      url: "/campaigns",
      icon: NotebookPenIcon,
    },
    {
      title: "Websites",
      url: "/sites",
      icon: GlobeIcon,
      feature: "SELLER_LEAD_CAPTURE_PAGES",
    },
    {
      title: "Power Dialer",
      url: "/power-dialer",
      icon: HeadsetIcon,
    },
    {
      title: "AI Agents",
      url: "/ai-agents",
      icon: BotIcon,
    },
    {
      title: "Integrations",
      url: "/integrations",
      icon: BlocksIcon,
      isActive: true,
    },
    {
      title: "Activity",
      url: "/activity",
      icon: ActivityIcon,
      isActive: true,
    },
  ],
  projects: [
    {
      name: "Design Engineering",
      url: "#",
      icon: Frame,
    },
    {
      name: "Sales & Marketing",
      url: "#",
      icon: PieChart,
    },
    {
      name: "Travel",
      url: "#",
      icon: Map,
    },
  ],
};

export function DashboardSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { data: userData, isLoading } = useGetHomeQuery();
  const { data: features } = useGetFeatureCatalogQuery();
  const navigate = useNavigate();

  const enabledFeatureKeys = new Set(
    features?.filter((feature) => feature.enabled).map((feature) => feature.key),
  );
  const visibleNavMain = data.navMain.filter(
    (item) => !item.feature || enabledFeatureKeys.has(item.feature),
  );

  useEffect(() => {
    if (!userData && !isLoading) {
      navigate("/login");

      return;
    }
  }, []);

  return (
    <>
      <Sidebar collapsible="icon" variant="sidebar" {...props}>
        <SidebarHeader>
          <SidebarOrganisationCard />
        </SidebarHeader>
        <SidebarContent>
          <DashboardNavMain items={visibleNavMain} />
          <SidebarSettingsMenu />
          {userData?.role === "PLATFORM_ADMIN" && <AdminMenu />}
        </SidebarContent>
        <SidebarFooter>{!isLoading && userData && <DashboardNavUser user={userData} />}</SidebarFooter>
        <SidebarRail />
      </Sidebar>
    </>
  );
}
